import Image from "next/image";
import { NextPage, GetStaticProps } from "next";

import { useTranslations } from "next-intl";

import Navbar from "../components/Navbar";
import BannerHeader from "../components/BannerHeader";

import Separator from "../public/separator.svg";
import Portrait from "../public/portrait.png";

import {
  getAboutBanner,
  getAbout,
  getAboutJourney,
  convertLocale,
  getAboutProfileImage,
  shouldDisplayChineseLocale,
} from "../util/service";
import { NextSeo } from "next-seo";
import { getPlaiceholder } from "plaiceholder";
import { optimizedImageProps } from "../types";

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const _locale = shouldDisplayChineseLocale(locale) ? "zh" : locale;
  const loc = convertLocale(locale);
  const [banner, aboutDescription, aboutJourney, profileImage] =
    await Promise.all([
      getAboutBanner(),
      getAbout(loc),
      getAboutJourney(loc),
      getAboutProfileImage(),
    ]);

  /* base64 image placeholder */

  // banner
  const bannerPlaiceholder = await getPlaiceholder(banner);
  const banner_optimized = {
    imageURL: bannerPlaiceholder.img.src,
    blurDataURL: bannerPlaiceholder.base64,
  };

  // profile image
  const profileImagePlaiceholder = await getPlaiceholder(profileImage);
  const profileImage_optimized = {
    imageURL: profileImagePlaiceholder.img.src,
    blurDataURL: profileImagePlaiceholder.base64,
  };

  return {
    props: {
      aboutDescription,
      aboutJourney,
      banner_optimized,
      profileImage_optimized,
      messages: (await import(`../locales/${_locale}/common.json`)).default,
    },
    revalidate: 60,
  };
};

const About: NextPage<{
  banner_optimized: optimizedImageProps;
  aboutDescription: string;
  aboutJourney: string[];
  profileImage_optimized: optimizedImageProps;
}> = ({
  banner_optimized,
  aboutDescription,
  aboutJourney,
  profileImage_optimized,
}) => {
  const t = useTranslations("About");
  return (
    <>
      <NextSeo
        title="About | Animenz Piano Sheet"
        description={aboutDescription.replace(/(<([^>]+)>)/gi, "")}
        openGraph={{
          images: [
            {
              url: banner_optimized.imageURL,
              width: 1200,
              height: 630,
              alt: "Animenz Piano Sheet",
            },
          ],
        }}
      />
      <Navbar />
      <div className="h-full w-screen">
        <BannerHeader
          img={banner_optimized}
          alt={t("altImage")}
          heading={t("title")}
          dark={true}
        />
        <div className="flex w-full flex-col items-center p-2 py-16 text-primary-light md:h-full">
          <div className="mx-auto w-full max-w-[1080px]">
            <div className="m-auto grid-cols-5 gap-4 md:grid lg:gap-12">
              <div className="col-span-2 m-auto max-w-[380px] p-5 py-10">
                <Image
                  src={profileImage_optimized.imageURL}
                  blurDataURL={profileImage_optimized.blurDataURL}
                  placeholder="blur"
                  width={400}
                  height={507}
                  alt="portrait of Animenz"
                  // className="border-2 border-secondary-taupe"
                />
              </div>
              <div className="col-span-3 flex flex-col justify-center">
                <h1 className="p-5 pt-10 font-libre-baskerville text-3xl font-bold lg:text-4xl">
                  {t("journeyText")}
                </h1>
                <ul className="list-disc p-5 text-base">
                  {aboutJourney.map((journey, index) => (
                    <li className="cursor-default py-3" key={index}>
                      <p>{journey}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="w-full max-w-[1080px]">
            <div className="mx-auto flex w-full max-w-[245px] justify-center py-10 md:max-w-[450px]">
              <Image
                src={Separator}
                alt="separator"
                style={{ width: "inherit || auto", height: "inherit || auto" }}
              />
            </div>
            <div
              className="about-content p-5 md:px-10"
              dangerouslySetInnerHTML={{ __html: aboutDescription }}
            ></div>
            <div className="mx-auto flex w-full max-w-[245px] justify-center py-10 md:max-w-[450px]">
              <Image
                src={Separator}
                alt="separator"
                style={{ width: "inherit || auto", height: "inherit || auto" }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
